import React, { useCallback, useEffect, useState } from 'react';
import { Form as FinalForm } from 'react-final-form';
import { Alert, Button, Container, Form } from 'reactstrap';
import FormFieldSwitchPill from '../../../../../components/formFields/FormFieldSwitchPill';
import { unwrapPayload } from '../../utilities';
import { WorkerHistorySection } from './WorkerHistorySection';
import DateTimeText from '../../../../../components/DateTimeText';

type SynchronizationSectionProperties =
{
    settings: any | null
    markVisibleProductsDirty: (values: any) => Promise<any>
    searchWorkerHistory: (values: any) => Promise<any>
    getOperationalSettings: () => Promise<any>
    onUpdateSettings: (values: any) => Promise<void> | void
}

function SynchronizationSection
(
    {
        settings,
        markVisibleProductsDirty,
        searchWorkerHistory,
        getOperationalSettings,
        onUpdateSettings
    }
    : SynchronizationSectionProperties
)
{
    const [lastError, setLastError] = useState<string | null>(null);
    const [queueResult, setQueueResult] = useState<any | null>(null);
    const [saveResult, setSaveResult] = useState<string | null>(null);
    const [operationalSettings, setOperationalSettings] = useState<any | null>(null);
    const [isQueueingVisibleProducts, setIsQueueingVisibleProducts] = useState(false);
    const [isSavingSynchronizationSettings, setIsSavingSynchronizationSettings] = useState(false);
    const [isLoadingOperationalSettings, setIsLoadingOperationalSettings] = useState(false);

    const loadOperationalSettings = useCallback(async () =>
    {
        setIsLoadingOperationalSettings(true);

        try
        {
            const result = await getOperationalSettings();
            setOperationalSettings(unwrapPayload(result));
        }
        catch (error: any)
        {
            setLastError(error?.message ?? 'Pobieranie ustawień operacyjnych zakończyło się błędem.');
        }
        finally
        {
            setIsLoadingOperationalSettings(false);
        }
    }, [getOperationalSettings]);

    useEffect(() =>
    {
        loadOperationalSettings();
    }, [loadOperationalSettings]);

    const queueVisibleProducts = async () =>
    {
        setLastError(null);
        setQueueResult(null);
        setIsQueueingVisibleProducts(true);

        try
        {
            const result = await markVisibleProductsDirty({ reason: 'ManualVisibleProductsRefresh' });
            setQueueResult(unwrapPayload(result));
        }
        catch (error: any)
        {
            setLastError(error?.message ?? 'Ponowne sprawdzenie produktów zakończyło się błędem.');
        }
        finally
        {
            setIsQueueingVisibleProducts(false);
        }
    };

    const saveSynchronizationSettings = async (values: any) =>
    {
        setLastError(null);
        setSaveResult(null);
        setIsSavingSynchronizationSettings(true);

        try
        {
            await onUpdateSettings
            ({
                ...(settings ?? {}),
                automaticSynchronizationEnabled: values.automaticSynchronizationEnabled,
                authMode: 'ServiceAccount',
                name: settings?.name || 'Google Merchant Center'
            });

            setSaveResult('Zapisano ustawienia automatycznego eksportu.');
            await loadOperationalSettings();
        }
        catch (error: any)
        {
            setLastError(error?.message ?? 'Zapis ustawień automatycznego eksportu zakończył się błędem.');
        }
        finally
        {
            setIsSavingSynchronizationSettings(false);
        }
    };

    const synchronizationInitialValues =
    {
        automaticSynchronizationEnabled: settings?.automaticSynchronizationEnabled === true
    };

    const summary = operationalSettings?.exportStateSummary || {};
    const currentRun = operationalSettings?.currentRun || null;
    const hasCurrentRun = !!currentRun?.runId;

    const renderCount = (value: any) => Number.isFinite(Number(value)) ? Number(value).toLocaleString() : '0';

    return (
        <Container fluid>
            {lastError && <Alert color="danger">{lastError}</Alert>}

            <section className="mb-5">
                <h5 className="mb-3">Automatyczny eksport produktów</h5>

                <FinalForm
                    key={String(settings?.automaticSynchronizationEnabled === true)}
                    initialValues={synchronizationInitialValues}
                    onSubmit={saveSynchronizationSettings}
                    render={({ handleSubmit }) => (
                        <Form onSubmit={handleSubmit}>
                            <div className="d-flex flex-wrap align-items-center justify-content-between border rounded p-3">
                                <div className="mb-3 mb-xl-0">
                                    <FormFieldSwitchPill
                                        name="automaticSynchronizationEnabled"
                                        label="Automatyczny eksport produktów"
                                        className=""
                                    />
                                    <div className="text-muted mt-2">
                                        Kolejne automatyczne uruchomienie: {operationalSettings?.automaticSynchronizationEnabled && operationalSettings?.workerEnabled
                                            ? <DateTimeText value={operationalSettings?.nextAutomaticRunAtUtc} />
                                            : '—'}
                                    </div>
                                    <div className="text-muted small">
                                        Ostatnie uruchomienie workera: {operationalSettings?.lastAutomaticRunStartedAtUtc ? <DateTimeText value={operationalSettings?.lastAutomaticRunStartedAtUtc} /> : '—'}
                                        {operationalSettings?.workerIntervalSeconds ? `, interwał: ${operationalSettings.workerIntervalSeconds}s` : ''}
                                    </div>
                                </div>

                                <div className="d-flex flex-wrap align-items-center">
                                    <Button
                                        type="button"
                                        color="secondary"
                                        className="mr-2 mb-2"
                                        disabled={isLoadingOperationalSettings}
                                        onClick={loadOperationalSettings}
                                    >
                                        {isLoadingOperationalSettings ? 'Odświeżanie...' : 'Odśwież stan'}
                                    </Button>
                                    <Button
                                        type="submit"
                                        color="primary"
                                        className="mb-2"
                                        disabled={isSavingSynchronizationSettings}
                                    >
                                        Zapisz
                                    </Button>
                                </div>
                            </div>
                        </Form>
                    )}
                />

                {saveResult && <Alert color="success" className="mt-3">{saveResult}</Alert>}
                {operationalSettings?.workerConfiguredEnabled === false && (
                    <Alert color="warning" className="mt-3">
                        Worker Google Merchant jest wyłączony w konfiguracji aplikacji. Sprawdź ustawienie GoogleMerchant:Worker:Enabled.
                    </Alert>
                )}
                {operationalSettings?.workerConfiguredEnabled !== false && operationalSettings?.workerProcessEnabled === false && (
                    <Alert color="warning" className="mt-3">
                        Worker Google Merchant jest wyłączony w bieżącym procesie.
                    </Alert>
                )}
            </section>

            <section className="mb-5">
                <h5 className="mb-3">Stan eksportu</h5>

                <div className="row">
                    <div className="col-6 col-xl-2 mb-3">
                        <div className="border rounded p-3 h-100">
                            <div className="text-muted small">Wymagają przeliczenia</div>
                            <div className="h4 mb-0">{renderCount(summary.dirtyCount)}</div>
                        </div>
                    </div>
                    <div className="col-6 col-xl-2 mb-3">
                        <div className="border rounded p-3 h-100">
                            <div className="text-muted small">Gotowe do wysłania</div>
                            <div className="h4 mb-0">{renderCount(summary.preparedCount)}</div>
                            <div className="text-muted small">Upsert: {renderCount(summary.preparedUpsertCount)}, delete: {renderCount(summary.preparedDeleteCount)}</div>
                        </div>
                    </div>
                    <div className="col-6 col-xl-2 mb-3">
                        <div className="border rounded p-3 h-100">
                            <div className="text-muted small">Wysłane</div>
                            <div className="h4 mb-0">{renderCount(summary.exportedCount)}</div>
                        </div>
                    </div>
                    <div className="col-6 col-xl-2 mb-3">
                        <div className="border rounded p-3 h-100">
                            <div className="text-muted small">Pominięte / wstrzymane</div>
                            <div className="h4 mb-0">{renderCount(summary.withheldCount ?? summary.skippedCount)}</div>
                            <div className="text-muted small">Stany: {renderCount(summary.skippedCount)}, wykluczone produkty: {renderCount(summary.categoryExcludedProductCount)}</div>
                        </div>
                    </div>
                    <div className="col-6 col-xl-2 mb-3">
                        <div className="border rounded p-3 h-100">
                            <div className="text-muted small">Błędy</div>
                            <div className="h4 mb-0">{renderCount(summary.failedCount)}</div>
                            <div className="text-muted small">Do ponowienia: {renderCount(summary.retryReadyFailedCount)}</div>
                        </div>
                    </div>
                    <div className="col-6 col-xl-2 mb-3">
                        <div className="border rounded p-3 h-100">
                            <div className="text-muted small">Usunięte z Google</div>
                            <div className="h4 mb-0">{renderCount(summary.deletedCount)}</div>
                        </div>
                    </div>
                </div>

                <div className="border rounded p-3">
                    <div className="d-flex flex-wrap align-items-start justify-content-between">
                        <div className="mr-3 mb-2">
                            <h6 className="mb-2">Bieżący cykl workera</h6>
                            {!hasCurrentRun && (
                                <div className="text-muted">Brak informacji o ostatnim cyklu w bieżącym procesie.</div>
                            )}
                            {hasCurrentRun && (
                                <>
                                    <div>
                                        Status: <strong>{currentRun.isRunning ? 'trwa' : 'zakończony'}</strong>
                                        {currentRun.phase ? `, faza: ${currentRun.phase}` : ''}
                                    </div>
                                    <div className="text-muted small">
                                        Start: <DateTimeText value={currentRun.startedAtUtc} />
                                        {currentRun.lastUpdatedAtUtc ? <> · aktualizacja: <DateTimeText value={currentRun.lastUpdatedAtUtc} /></> : null}
                                    </div>
                                    <div className="text-muted small mt-1">
                                        {currentRun.message}
                                    </div>
                                    <div className="mt-2 small">
                                        Iteracje: przygotowanie {renderCount(currentRun.prepareIterations)}, wysyłka {renderCount(currentRun.sendIterations)} ·
                                        Prepared {renderCount(currentRun.preparedCount)}, Sent {renderCount(currentRun.sentCount)},
                                        Skipped {renderCount(currentRun.skippedCount)}, Failed {renderCount(currentRun.failedCount)},
                                        Requeued {renderCount(currentRun.requeuedCount)}, Unchanged {renderCount(currentRun.unchangedCount)}
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="text-muted small mb-2">
                            Paczka prepare: {renderCount(operationalSettings?.prepareBatchSize)}, paczka send: {renderCount(operationalSettings?.sendBatchSize)},
                            limit cyklu: prepare {renderCount(operationalSettings?.maxPreparePerRun)}, send {renderCount(operationalSettings?.maxSendPerRun)},
                            czas: {renderCount(operationalSettings?.maxRunSeconds)}s
                        </div>
                    </div>
                </div>
            </section>

            <section className="mb-5">
                <div className="border rounded p-3">
                    <div className="d-flex flex-wrap align-items-start justify-content-between">
                        <div className="mr-3 mb-3">
                            <h5 className="mb-2">Ponowna ocena produktów</h5>
                            <p className="text-muted mb-0">
                                Wymusza ponowną ocenę widocznych produktów dla aktywnych profili eksportu.
                                Produkty bez zmian nie zostaną ponownie wysłane do Google Merchant.
                            </p>
                        </div>

                        <Button
                            type="button"
                            color="secondary"
                            className="mb-2"
                            disabled={isQueueingVisibleProducts}
                            onClick={queueVisibleProducts}
                        >
                            Sprawdź ponownie widoczne produkty
                        </Button>
                    </div>
                </div>
            </section>

            {queueResult && (
                <Alert color={queueResult.success ? 'success' : 'warning'} className="mt-3">
                    {queueResult.message}
                </Alert>
            )}

            <WorkerHistorySection searchWorkerHistory={searchWorkerHistory} />
        </Container>
    );
}

export default SynchronizationSection;
